'use client';

import { Position, WISHLIST_MODAL_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import {
    SideMenuModal,
    UICarousel,
    UICarouselDelayer,
    useTailwindBreakpoint,
    useUIContext,
} from '@headless-workspace/core-ui';
import {
    GlowProductTile,
    Icons,
    ModalCommonClasses,
    PropsWithClassName,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import type { Component } from '@headless-workspace/typings';
import { useIsNotifyMeEnabled, useProductTilesMapper, useWishlist, WishlistModal } from '../../../client';
import type { TileProductValue, VipBrandsValue } from '../../../server';
import { useProductTileCTABusinessRules } from './useProductTileCTABusinessRules';

type ProductCarouselProps = PropsWithClassName &
    PropsWithLocale & {
        products: TileProductValue[];
        vipBrands: VipBrandsValue;
        prevBtnAriaLabel: string;
        nextBtnAriaLabel: string;
        wishlistBtnAriaLabel: string;
        thumbArialLabel: string;
        navigationButtonClassName?: string;
        enablePriceExceptionLegalMentions?: boolean;
        shouldDelay?: boolean;
    };

export const ProductDetailCarousel: Component<ProductCarouselProps> = ({
    className,
    products,
    vipBrands,
    prevBtnAriaLabel,
    nextBtnAriaLabel,
    wishlistBtnAriaLabel,
    thumbArialLabel,
    navigationButtonClassName,
    locale,
    enablePriceExceptionLegalMentions = false,
    shouldDelay = true,
}) => {
    const isTablet = useTailwindBreakpoint('tablet');
    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();

    const { shouldAddToWishlist, update: updateWishlist, isProductInWishlist } = useWishlist();

    const { result: isNotifyMeEnabledResult } = useIsNotifyMeEnabled();
    const isNotifyMeEnabled = unwrapResultSuccess(isNotifyMeEnabledResult) ?? false;

    const { handleProductModalView, isAddingToBasket } = useProductTileCTABusinessRules({
        vipBrands,
        locale,
        isNotifyMeEnabled,
        addIfAbsentAndOpen,
    });

    const productTiles = useProductTilesMapper(products, locale, true, enablePriceExceptionLegalMentions);
    const CarouselComponent = shouldDelay ? UICarouselDelayer : UICarousel;

    return (
        <CarouselComponent
            className={className}
            items={productTiles}
            renderItem={(product, isInView) => {
                const buttonOnClick = handleProductModalView(product.isAvailable ?? false, product);
                const icon = isNotifyMeEnabled && !product.isAvailable ? Icons.Notification : undefined;

                return (
                    <GlowProductTile
                        {...product}
                        button={{
                            ...product.button,
                            onClick: buttonOnClick,
                            icon,
                            isLoading: isAddingToBasket(product.sku),
                        }}
                        isTablet={isTablet}
                        isLoading={!isInView}
                        wishlist={{
                            onClick: () => {
                                if (shouldAddToWishlist) {
                                    updateWishlist({
                                        productId: product.id,
                                        productSku: product.sku,
                                        productName: product.name,
                                    });
                                } else {
                                    addIfAbsentAndOpen(
                                        WISHLIST_MODAL_ID,
                                        SideMenuModal(
                                            Position.None,
                                            {
                                                default: <WishlistModal productSku={product.sku} locale={locale} />,
                                            },
                                            ModalCommonClasses,
                                        ),
                                    );
                                }
                            },
                            isAdded: isProductInWishlist(product.sku),
                            ariaLabel: wishlistBtnAriaLabel,
                        }}
                        legalMentionsLabel={product.legalMentionsLabel}
                    />
                );
            }}
            prevBtnAriaLabel={prevBtnAriaLabel}
            nextBtnAriaLabel={nextBtnAriaLabel}
            thumbArialLabel={thumbArialLabel}
            navigationButtonClassName={navigationButtonClassName}
        />
    );
};
